import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import toast from "react-hot-toast";
import { getRandomColor } from "js/helpers/random_color";
import { Snackbar, Alert, Switch } from "@mui/material";
import { generateUUID, hashCode } from "js/helpers/common_functions";
import signalRService from "js/services/SignalRService";
import "style.css";
import callingTone from "../../audio/calling_tone.mp3";

import {
  MoreVert,
  KeyboardVoiceRounded,
  MicOffRounded,
  VideocamRounded,
  VideocamOffRounded,
  CallEndRounded,
  PictureInPictureRounded,
  PeopleRounded,
  CloseRounded,
  CallRounded,
  Translate,
  ScreenShareRounded,
    StopScreenShareRounded, RadioButtonChecked, RadioButtonCheckedRounded
} from "@mui/icons-material";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
} from "@mui/material";

import {
  initiateGroupCall,
  initiateEventCall,
  callDevice,
  getCallToken,
  fetchDevice,
  fetchLinkedDevices,
  fetchPolicyDevices,
  fetchEvent,
  muteParticipant,
  resendNotification,
  connectSignalR,
  fetchActiveUsers,
  startCallStatusCheck,
    disconnectSignalR, startVideoRecording, updateVideoRecordingState, stopVideoRecording
} from "js/Api/index";
import SideImage from "images/side_image.png";
import { languages } from "js/Constants/common";
import {
  startTranscription,
  stopTranscription,
  updateDeviceProfile,
  updateUserProfile,
} from "js/Api/videocall";

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const AGORA_BOT_UIDS = [1234, 2000, 3000];
const SCREEN_SHARE_UID = 12345;

// Helper function to check if a user is a bot
const isAgoraBot = (uid) => {
  return AGORA_BOT_UIDS.includes(Number(uid));
};

const isScreenShareUser = (uid) => {
  return Number(uid) === SCREEN_SHARE_UID;
};

// Helper function to filter out bot users from array
const filterBotUsers = (users) => {
  return users.filter((user) => !isAgoraBot(user.uid) && !isScreenShareUser(user.uid));
};

function AgoraVideoCall({
  userInfo,
  callingDeviceId,
  callingAccountId,
  callerName,
  policyId,
  eventId,
  isJoiningEvent,
  isEventCall,
  isGroupCall,
  onClose,
  localVideoTrack,
  localAudioTrack,
  initialCameraEnabled,
  initialMicrophoneEnabled,
  audiosource,
  videosource,
  audioOutput,
  answerName,
  setLiveTranslation,
  liveTranslation,
  showSubTitle,
  setShowSubTitle,
  setYourPreferredLanguage,
  yourPreferredLanguage,
  setSelectedDevicePreferredLanguage,
  selectedDevicePreferredLanguage,
  messages,
  isTranscriptionEnabled,
  call_id,
  playCallingTone = false,
}) {
  const [joined, setJoined] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isCameraEnabled, setIsCameraEnabled] = useState(initialCameraEnabled);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(
    initialMicrophoneEnabled
  );
  const [localVideoEnabled, setLocalVideoEnabled] =
    useState(initialCameraEnabled);

    const [isVideoRecordingAllowed, setisVideoRecordingAllowed] = useState(false);
    const [isVideoRecording, setisVideoRecording] = useState(false);
    const isVideoRecordingRef = useRef(false);  

  const clientRef = useRef(null);
  const isJoiningRef = useRef(false);
  const localUserIdRef = useRef(null);
  const localVideoRef = useRef(null);
  const [remoteCameraStates, setRemoteCameraStates] = useState({});
  const [remoteMicrophoneStates, setRemoteMicrophoneStates] = useState({});
  const remoteVideoRefs = useRef({});
  const [showAlert, setShowAlert] = useState(false);
  const [callState, setCallState] = useState("waiting");
  const [callerId, setCallerId] = useState(null);
  const callerIdRef = useRef(null);
  const [callActive, setCallActive] = useState(true);

  const [activeSpeakers, setActiveSpeakers] = useState(new Set());
  const speakerTimeoutsRef = useRef({});

  const aiJoinedRef = useRef(false);
  const transcriptionActiveRef = useRef(false);

  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState(audiosource);
  const [selectedVideo, setSelectedVideo] = useState(videosource);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(audioOutput);
  const [currentAudioTrack, setCurrentAudioTrack] = useState(localAudioTrack);
  const [currentVideoTrack, setCurrentVideoTrack] = useState(localVideoTrack);

  const [defaultDevices, setDefaultDevices] = useState({
    audioInput: null,
    audioOutput: null,
    camera: null,
  });

  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const optionsDialogRef = useRef(null);

  const translationDialogRef = useRef(null);

  const [countdown, setCountdown] = useState(5);
  const countdownTimerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showLocalVideo, setShowLocalVideo] = useState(true);
  const [showTranslation, setShowTranslation] = useState(false);

  const [showParticipants, setShowParticipants] = useState(false);
  const [participantTabValue, setParticipantTabValue] = useState(0);

  const [onCallParticipants, setOnCallParticipants] = useState([]);
  const [invitedParticipants, setInvitedParticipants] = useState([]);
  const [familyDeviceIds, setFamilyDeviceIds] = useState(new Set());
  const [deviceAgoraUidMap, setDeviceAgoraUidMap] = useState({});
  const [agoraUidToDeviceInfoMap, setAgoraUidToDeviceInfoMap] = useState({});
  const agoraUidToDeviceInfoMapRef = useRef({});
  const deviceAgoraUidMapRef = useRef({});
  const [activeUserNameMap, setActiveUserNameMap] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });

  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const isScreenSharingRef = useRef(false);
  const [screenShareTrack, setScreenShareTrack] = useState(null);
  const screenVideoRef = useRef(null);
  const isStoppingScreenShareRef = useRef(false);
  const screenVideoTrackRef = useRef(null);
  const screenAudioTrackRef = useRef(null);

  const [isRemoteScreenSharing, setIsRemoteScreenSharing] = useState(false);
  const [screenShareUserName, setScreenShareUserName] = useState("");
  const [remoteScreenShareUser, setRemoteScreenShareUser] = useState(null);
  const [screenShareClient, setScreenShareClient] = useState(null);
  const screenShareClientRef = useRef(null);

  const [localVideoPosition, setLocalVideoPosition] = useState({
    top: isMobile ? "10px" : "20px",
    right: isMobile ? "10px" : "20px",
    left: "auto",
    bottom: "auto",
  });

  const callingToneRef = useRef(null);

  const stopCallingTone = useCallback(() => {
    if (callingToneRef.current) {
      callingToneRef.current.pause();
      callingToneRef.current.currentTime = 0;
    }
  }, []);

  const [showSetDefaultBtn, setShowSetDefaultBtn] = useState(false); // Use this state for enabling the "Set as Default" button when change your preferred language

  const userProfile = JSON.parse(localStorage.getItem("userInfo")); // Get user profile from local storage

  const dragStartPos = useRef({ x: 0, y: 0 });
  const initialPos = useRef({ top: 0, right: 0, left: 0, bottom: 0 });
  const isDraggingRef = useRef(false);

  const isGuest = userInfo?.isExternalUser === true; // guest 判定
  const { effectiveUserId, localAgoraUid } = useMemo(() => {
    const uidStr = isGuest ? generateUUID() : userInfo.userId;
    const hash = Math.abs(hashCode(uidStr));
    let agoraUID = 140310 + hash;
    if (userInfo?.agoraUid !== null && userInfo?.agoraUid !== undefined) {
      agoraUID = userInfo.agoraUid;
    }

    return {
      effectiveUserId: uidStr,
      localAgoraUid: agoraUID, // Agora int UID
    };
  }, [userInfo]);

  const filteredRemoteUsers = useMemo(() => {
    const filtered = filterBotUsers(remoteUsers);

    const uniqueUsers = [];
    const seenUIDs = new Set();

    filtered.forEach((user) => {
      if (!seenUIDs.has(user.uid)) {
        seenUIDs.add(user.uid);
        uniqueUsers.push(user);
      }
    });

    return uniqueUsers;
  }, [remoteUsers]);

  const handleVolumeIndicator = (result) => {
    // console.log(result);

    const volumeThreshold = 55;
    const activeSpeakerUIDs = new Set();

    result.forEach((volume) => {
      if (volume.level > volumeThreshold) {
        activeSpeakerUIDs.add(volume.uid);
      }
    });

    Object.keys(speakerTimeoutsRef.current).forEach((uid) => {
      if (!activeSpeakerUIDs.has(Number(uid))) {
        clearTimeout(speakerTimeoutsRef.current[uid]);
        delete speakerTimeoutsRef.current[uid];
      }
    });

    setActiveSpeakers(activeSpeakerUIDs);

    // Set timeouts for all currently active speakers
    activeSpeakerUIDs.forEach((uid) => {
      // Clear existing timeout for this user if it exists
      if (speakerTimeoutsRef.current[uid]) {
        clearTimeout(speakerTimeoutsRef.current[uid]);
      }

      // Set new timeout
      speakerTimeoutsRef.current[uid] = setTimeout(() => {
        setActiveSpeakers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uid);
          return newSet;
        });
        delete speakerTimeoutsRef.current[uid];
      }, 2000);
    });
  };

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  useEffect(() => {
  if (!playCallingTone) {
    return;
  }

  if (callState === "waiting") {
    if (!callingToneRef.current) {
      const audio = new Audio(callingTone);
      audio.loop = true;
      callingToneRef.current = audio;
    }

    callingToneRef.current
      .play()
      .catch((err) => console.warn("Failed to play calling tone:", err));
  } else {
    stopCallingTone();
  }

  return () => {
    if (callState !== "waiting") {
      stopCallingTone();
    }
  };
}, [playCallingTone, callState, stopCallingTone]);

  useEffect(() => {
    const hasAIUser = remoteUsers.some((user) => isAgoraBot(user.uid));
    aiJoinedRef.current = hasAIUser;
  }, [remoteUsers]);

  useEffect(() => {
    transcriptionActiveRef.current = liveTranslation;
  }, [liveTranslation]);

  useEffect(() => {
    if (
      joined &&
      filteredRemoteUsers.length === 0 &&
      callState === "connected"
    ) {
        setCallState("ending");

        if (isVideoRecording === true) {
            stopVideoRecording(callerId)
        }

    } else if (
      joined &&
      filteredRemoteUsers.length > 0 &&
      callState !== "ending"
    ) {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setShowAlert(false);
      setCountdown(5);

      setCallState("connected");
    }
  }, [joined, filteredRemoteUsers, callState]);

  useEffect(() => {
    if (callActive === false) {
      if (filteredRemoteUsers.length === 0) {
        // console.log("Call declined: active=false and no remote users");
        setShowAlert(true);

        const dialogTitle = document.getElementById("alert-dialog-title");
        if (dialogTitle) {
          dialogTitle.textContent = "Call Declined";
        }

        const dialogContent = document.querySelector(
          "#alert-dialog-description"
        );
        if (dialogContent) {
          dialogContent.textContent =
            "The other participant has declined the call. The video call will close in " +
            countdown +
            " seconds.";
        }

          if (isVideoRecording === true) {
              stopVideoRecording(callerId)
          }

        setCallState("ending");
      }
    }
  }, [callActive, filteredRemoteUsers.length]);

  useEffect(() => {
    if (callState === "ending") {
      // console.log("Detected callState=ending, start countdown...");
      setShowAlert(true);
      setCountdown(5);

      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }

      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            handleLeaveChannel();
            return 0;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [callState]);

  useEffect(() => {
    // console.log("Current userInfo:", userInfo);
    agoraUidToDeviceInfoMapRef.current = agoraUidToDeviceInfoMap;
  }, [agoraUidToDeviceInfoMap]);

  useEffect(() => {
    deviceAgoraUidMapRef.current = deviceAgoraUidMap;
  }, [deviceAgoraUidMap]);

  useEffect(() => {
    const fetchInvitedDevices = async () => {
      try {
        let mainDevices = [];
        let linkedDevices = [];

        if (isGroupCall && policyId) {
          const result = await fetchPolicyDevices(policyId);
          // console.log("Policy devices result:", result);
          if (result.success && result.data) {
            mainDevices = result.data;
          }
        } else if (isEventCall && eventId) {
          const result = await fetchEvent(eventId);
          // console.log("Event result:", result);
            if (result.success) {

                if (!isGuest) {
                    setisVideoRecordingAllowed(true)
                }

            if (result.device) {
              mainDevices = result.device;
            } else if (result.data) {
              if (Array.isArray(result.data) && result.data[0]?.device) {
                mainDevices = result.data[0].device;
              } else if (result.data.device) {
                mainDevices = result.data.device;
              }
            }
          }
        } else if (callingDeviceId) {
          const result = await fetchDevice(callingDeviceId);
          if (result.success && result.data) {
            mainDevices = [result.data];
          }
          const resLinked = await fetchLinkedDevices(callingDeviceId);
          if (resLinked.success && Array.isArray(resLinked.data)) {
            linkedDevices = resLinked.data.filter(
              (d) => d.id !== callingDeviceId
            );
            setFamilyDeviceIds(new Set(linkedDevices.map((d) => d.id)));
          }
        }

        // console.log("All devices:", devices);

        // console.log("Filtered devices (excluding caller):", filteredDevices);
        const allForMapping = [...mainDevices, ...linkedDevices];

        // Create mappings for device IDs to Agora UIDs
        const deviceToUidMap = {};
        const uidToDeviceMap = {};

        allForMapping.forEach((device) => {
          if (!device || device.id === userInfo.id) return;

          const agoraUid = device.agoraUid;
          deviceToUidMap[device.id] = agoraUid;
          uidToDeviceMap[agoraUid] = {
            id: device.id,
            name: device.name,
            profilePicture: device.profilePicture || null,
            isFamilyMember: linkedDevices.some((d) => d.id === device.id),
          };
        });

        // console.log("Device to Agora UID map:", deviceToUidMap);
        // console.log("Agora UID to Device map:", uidToDeviceMap);

        setDeviceAgoraUidMap(deviceToUidMap);
        setAgoraUidToDeviceInfoMap(uidToDeviceMap);

        // Initially, all devices are in invited list
        const invitedList = mainDevices.map((device) => ({
          id: device.id,
          name: device.name,
          agoraUid: device.agoraUid,
          profilePicture: device.profilePicture || null,
          status: "invited",
        }));

        // console.log("Initial invited list:", invitedList);
        setInvitedParticipants(invitedList);
      } catch (error) {
        console.error("Error fetching invited devices:", error);
      }
    };

    if (isGroupCall || isEventCall || callingDeviceId) {
      fetchInvitedDevices();
    }
  }, [isGroupCall, isEventCall, policyId, eventId, callingDeviceId]);

  // Update the participant lists management
  useEffect(() => {
    const updateParticipantLists = () => {
      // Update on-call participants
      const onCall = [
        {
          id: localUserIdRef.current || 1,
          deviceId: null,
          name: userInfo.name,
          profilePicture: userInfo.profilePicture || null,
          isMicOn: isMicrophoneEnabled,
          isCameraOn: isCameraEnabled,
          isMuted: false,
          isLocal: true,
        },
      ];

      // Add remote users to on-call list
      filteredRemoteUsers.forEach((user) => {
        const deviceInfo = agoraUidToDeviceInfoMap[user.uid];
        const activeInfo = activeUserNameMap[user.uid];

        const deviceId = deviceInfo?.id || activeInfo?.deviceId || null;
        const isFamily = familyDeviceIds.has(deviceId);

        if (deviceInfo || activeInfo) {
          onCall.push({
            id: user.uid,
            deviceId: deviceInfo?.id || activeInfo?.deviceId || null,
            name: activeInfo?.displayName || deviceInfo?.name || `Guest`,
            profilePicture: deviceInfo?.profilePicture || null,
            isMicOn: remoteMicrophoneStates[user.uid] !== false,
            isCameraOn: remoteCameraStates[user.uid] || false,
            isMuted: false,
            isLocal: false,
            isKnownDevice: true,
            connectionId: activeInfo?.connectionId || null,
            isFamilyMember: isFamily,
          });
        } else {
          // Handle unknown users
          onCall.push({
            id: user.uid,
            deviceId: null,
            name: `Guest User`,
            profilePicture: null,
            isMicOn: remoteMicrophoneStates[user.uid] !== false,
            isCameraOn: remoteCameraStates[user.uid] || false,
            isMuted: false,
            isLocal: false,
            isKnownDevice: false,
          });
        }
      });

      setOnCallParticipants(onCall);

      const onCallDeviceIds = new Set(
        onCall.filter((p) => p.deviceId).map((p) => p.deviceId)
      );

      setInvitedParticipants((prevInvitedParticipants) => {
        return prevInvitedParticipants.filter(
          (participant) => !onCallDeviceIds.has(participant.id)
        );
      });
    };

    updateParticipantLists();
  }, [
    filteredRemoteUsers,
    userInfo.name,
    isMicrophoneEnabled,
    isCameraEnabled,
    remoteCameraStates,
    remoteMicrophoneStates,
    agoraUidToDeviceInfoMap,
    callingDeviceId,
    answerName,
    activeUserNameMap,
  ]);

  const getGridLayout = (numUsers, hasScreenShare = false) => {
    if (hasScreenShare) {
      // When screen sharing, use a different layout
      if (numUsers <= 1) return { cols: 1, rows: 1 };
      if (numUsers <= 3) return { cols: 3, rows: 1 }; // Horizontal layout for participants
      if (numUsers <= 6) return { cols: 6, rows: 1 };
      return { cols: 6, rows: 2 }; // Maximum 2 rows for participants when screen sharing
    }

    if (numUsers <= 1) return { cols: 1, rows: 1 };
    if (numUsers <= 2) return { cols: 2, rows: 1 };
    if (numUsers <= 4) return { cols: 2, rows: 2 };
    if (numUsers <= 6) return { cols: 3, rows: 2 };
    if (numUsers <= 9) return { cols: 3, rows: 3 };
    if (numUsers <= 12) return { cols: 4, rows: 3 };
    if (numUsers <= 16) return { cols: 4, rows: 4 };
    if (numUsers <= 20) return { cols: 5, rows: 4 };
    if (numUsers <= 25) return { cols: 5, rows: 5 };
    if (numUsers <= 30) return { cols: 6, rows: 5 };
    return { cols: 6, rows: 6 }; // 36 users maximum
  };

  const switchTrack = async (deviceType, deviceId) => {
  try {
    if (deviceType === "audio" && deviceId !== null) {
      // console.log('Switching Audio Input Device:', {
      //   requestedDeviceId: deviceId || 'System Default',
      //   timestamp: new Date().toISOString()
      // });

      await clientRef.current.unpublish(currentAudioTrack);

      let effectiveAudioId = deviceId;
      
      if (!deviceId || deviceId === "") {
        const devices = await AgoraRTC.getMicrophones();

        const defaultDevice = devices.find(d => d.deviceId === "default");
        
        if (defaultDevice) {
          // console.log('Switching to explicit default audio device:', defaultDevice.label);
          effectiveAudioId = "default";
        } else {
          // console.log('No explicit default device found, letting browser choose');
          effectiveAudioId = undefined;
        }
      }

      const newTrack = effectiveAudioId 
        ? await AgoraRTC.createMicrophoneAudioTrack({
            microphoneId: effectiveAudioId,
          })
        : await AgoraRTC.createMicrophoneAudioTrack();

      // console.log('Audio Track Switched:', {
      //   requestedDeviceId: deviceId || 'System Default',
      //   effectiveDeviceId: effectiveAudioId || 'undefined (browser default)',
      //   actualTrackLabel: newTrack.getTrackLabel(),
      //   actualTrackId: newTrack.getTrackId(),
      //   timestamp: new Date().toISOString()
      // });

      setCurrentAudioTrack(newTrack);

      // If microphone is enabled, publish the new track
      if (isMicrophoneEnabled && joined) {
        await clientRef.current.publish(newTrack);
      }
    }

    if (deviceType === "video") {
      // console.log('Switching Camera Device:', {
      //   requestedDeviceId: deviceId || 'System Default',
      //   timestamp: new Date().toISOString()
      // });

      // First stop the local video preview
      if (currentVideoTrack) {
        currentVideoTrack.stop();
        await clientRef.current.unpublish(currentVideoTrack);
      }

      let newTrack;

      let effectiveVideoId = deviceId;
      
      if (!deviceId || deviceId === "") {
        const devices = await AgoraRTC.getCameras();
        const defaultDevice = devices.find(d => d.deviceId === "default");
        
        if (defaultDevice) {
          // console.log('Switching to explicit default camera device:', defaultDevice.label);
          effectiveVideoId = "default";
        } else {
          // console.log('No explicit default camera found, letting browser choose');
          effectiveVideoId = undefined;
        }
      }

      if (effectiveVideoId) {
        newTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            width: 640,
            height: 360,
            frameRate: 15,
          },
          cameraId: effectiveVideoId,
        });
      } else {
        newTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            width: 640,
            height: 360,
            frameRate: 15,
          },
        });
      }

      // console.log('Camera Track Switched:', {
      //   requestedDeviceId: deviceId || 'System Default',
      //   effectiveDeviceId: effectiveVideoId || 'undefined (browser default)',
      //   actualTrackLabel: newTrack.getTrackLabel(),
      //   actualTrackId: newTrack.getTrackId(),
      //   timestamp: new Date().toISOString()
      // });

      // Important: Play the local video first to ensure local preview works
      if (localVideoRef.current) {
        try {
          newTrack.play(localVideoRef.current);
          setLocalVideoEnabled(true);
        } catch (error) {
          console.error("Failed to play local video:", error);
        }
      }

      // Then update the state and publish
      setCurrentVideoTrack(newTrack);

      // If camera is enabled, publish the new track
      if (isCameraEnabled && joined) {
        await newTrack.setEnabled(true);
        await clientRef.current.publish(newTrack);
      }
    }
  } catch (error) {
    console.error("Failed to switch device:", error);
  }
};

  // Screen share functions
  const startScreenShare = async () => {
    try {
      setIsScreenSharing("loading");
      isScreenSharingRef.current = "loading";

      const ret = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: {
          width: 640,
          height: 360,
          frameRate: 20,
          bitrateMax: 1000,
        },
      });

      const videoTrack = Array.isArray(ret) ? ret[0] : ret;
      const audioTrack = Array.isArray(ret) ? ret[1] : null;

      screenVideoTrackRef.current = videoTrack;
      screenAudioTrackRef.current = audioTrack;

      const screenClient = AgoraRTC.createClient({ codec: "vp8", mode: "rtc" });
      screenShareClientRef.current = screenClient;

      let callType, id;
      if (isEventCall) {
        callType = "event";
        id = eventId;
      } else if (isGroupCall) {
        callType = "group";
        id = policyId;
      } else {
        callType = "device";
        id = callingDeviceId;
      }

      const tokenResult = await getCallToken(callType, id);
      if (!tokenResult.success) {
        throw new Error(tokenResult.error);
      }

      let channelId;
      if (isEventCall) {
        channelId = eventId;
      } else if (isGroupCall) {
        channelId = policyId;
      } else {
        channelId = callingAccountId;
      }

      await screenClient.join(
        process.env.REACT_APP_AGORA_APP_ID,
        channelId,
        tokenResult.token,
        SCREEN_SHARE_UID
      );

      // Handle the case where user cancels screen share selection
      videoTrack.on("track-ended", () => {
        console.log("Screen share ended by user or system");
        if (!isStoppingScreenShareRef.current) stopScreenShare();
      });

      // Publish screen share track
      const toPublish = audioTrack ? [videoTrack, audioTrack] : [videoTrack];
      await screenClient.publish(toPublish);

      if (clientRef.current) {
        try {
          const message = JSON.stringify({
            type: "SCREEN_SHARE_START",
            userName: userInfo.name,
            uid: localAgoraUid,
            timestamp: Date.now(),
          });

          // Try to send message directly without creating data stream first
          await clientRef.current.sendStreamMessage(message);
          console.log("Screen share start message sent:", message);
        } catch (error) {
          console.error("Failed to send screen share start message:", error);
          // Continue without stream message - not critical for core functionality
        }
      }

      setIsScreenSharing(true);
      isScreenSharingRef.current = true;

      // Play screen share locally for preview
      setTimeout(() => {
        if (screenVideoRef.current && videoTrack) {
          try {
            videoTrack.play(screenVideoRef.current);
          } catch (e) {
            console.error("Failed to play screen share preview:", e);
          }
        }
      }, 100);

      console.log("Screen sharing started successfully");
    } catch (error) {
      console.error("Failed to start screen share:", error);

      if (screenVideoTrackRef.current) {
        try {
          screenVideoTrackRef.current.stop();
          screenVideoTrackRef.current.close &&
            screenVideoTrackRef.current.close();
        } catch {}
        screenVideoTrackRef.current = null;
      }

      if (screenAudioTrackRef.current) {
        try {
          screenAudioTrackRef.current.stop();
          screenAudioTrackRef.current.close &&
            screenAudioTrackRef.current.close();
        } catch {}
        screenAudioTrackRef.current = null;
      }

      if (screenShareClientRef.current) {
        try {
          await screenShareClientRef.current.leave();
        } catch {}
        screenShareClientRef.current = null;
        setScreenShareClient(null);
      }
      setIsScreenSharing(false);
      isScreenSharingRef.current = false;
      toast.error("Failed to start screen sharing");
    }
  };

  const stopScreenShare = async () => {
    const screenClient = screenShareClientRef.current || screenShareClient;
    const videoTrack = screenVideoTrackRef.current || screenShareTrack;
    const audioTrack = screenAudioTrackRef.current || null;

    if (!videoTrack && !screenClient) {
      setIsScreenSharing(false);
      isScreenSharingRef.current = false;
      return;
    }

    try {
      isStoppingScreenShareRef.current = true;

      if (clientRef.current) {
        try {
          const message = JSON.stringify({
            type: "SCREEN_SHARE_STOP",
            userName: userInfo.name,
            uid: localAgoraUid,
            timestamp: Date.now(),
          });

          // Send message directly
          await clientRef.current.sendStreamMessage(message);
          console.log("Screen share stop message sent:", message);
        } catch (error) {
          console.error("Failed to send screen share stop message:", error);
          // Continue with cleanup even if message fails
        }
      }

      if (screenClient && videoTrack) {
        try {
          await screenClient.unpublish(
            [videoTrack, audioTrack].filter(Boolean)
          );
        } catch (err) {
          console.warn("Failed to unpublish tracks:", err);
        }
      }

      if (videoTrack) {
        try {
          videoTrack.stop();
          videoTrack.close && videoTrack.close();
        } catch {}
      }

      if (audioTrack) {
        try {
          audioTrack.stop();
          audioTrack.close && audioTrack.close();
        } catch {}
      }

      if (screenClient) {
        try {
          await screenClient.leave();
        } catch (err) {
          console.warn("Failed to leave screen share channel:", err);
        }
      }

      screenVideoTrackRef.current = null;
      screenAudioTrackRef.current = null;
      screenShareClientRef.current = null;
      setScreenShareClient(null);
      setScreenShareTrack(null);
      setIsScreenSharing(false);
      isScreenSharingRef.current = false;

      console.log("Screen sharing stopped successfully");
    } catch (error) {
      console.error("Failed to stop screen share:", error);
      setIsScreenSharing(false);
      isScreenSharingRef.current = false;
    } finally {
      isStoppingScreenShareRef.current = false;
    }
  };

  const setLocalVideoContainer = useCallback(
    (node) => {
      localVideoRef.current = node;

      if (!node) return;

      if (currentVideoTrack) {
        try {
          currentVideoTrack.stop();

          if (isCameraEnabled && showLocalVideo) {
            currentVideoTrack.play(node);
            setLocalVideoEnabled(true);
          } else {
            setLocalVideoEnabled(false);
          }
        } catch (err) {
          console.error("Re-play local video failed:", err);
          setLocalVideoEnabled(false);
        }
      }
    },
    [currentVideoTrack, isCameraEnabled, showLocalVideo]
  );

  const handleCallAgain = async (deviceId) => {
    try {
      // Get the device name for the toast message
      const deviceName =
        invitedParticipants.find((p) => p.id === deviceId)?.name || "device";

      // Determine which API to use based on call type
      if (isGroupCall || isEventCall) {
        const response = await resendNotification(
          userInfo.name,
          deviceId,
          isGroupCall ? policyId : eventId,
          isGroupCall
        );

        if (response.success) {
          // console.log(`Successfully called device again: ${deviceId}`);
          setSnackbar({
            open: true,
            message: `Call to ${deviceName} sent successfully!`,
            severity: "success",
          });
        } else {
          console.error(
            `Failed to call device again: ${deviceId}`,
            response.error
          );
          setSnackbar({
            open: true,
            message: `Failed to call ${deviceName}`,
            severity: "error",
          });
        }
      } else {
        // For regular one-on-one calls
        const result = await callDevice(deviceId, userInfo.name);

        if (result.success) {
          // console.log(`Successfully called device: ${deviceId}`);
          toast.success(`Call to ${deviceName} sent successfully!`);
        } else {
          console.error(`Failed to call device: ${deviceId}`, result.error);
          toast.error(`Failed to call ${deviceName}`);
        }
      }
    } catch (error) {
      console.error("Error calling device again:", error);
      toast.error("Failed to make call");
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return; // Don't close if user clicks away
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAudioInputChange = async (event) => {
    const newDeviceId = event.target.value;
    setSelectedAudioInput(newDeviceId);
    await switchTrack("audio", newDeviceId);
  };

  const handleCameraChange = async (event) => {
    //setter(event.target.value);
    const newDeviceId = event.target.value;
    setSelectedVideo(newDeviceId);
    await switchTrack("video", newDeviceId);
  };

  const handleAudioOutputChange = async (event, setter) => {
    const newDeviceId = event.target.value;
    setter(newDeviceId);

    try {
      // Change the audio output for all remote users
      for (const user of filteredRemoteUsers) {
        if (user.audioTrack) {
          await user.audioTrack.setPlaybackDevice(newDeviceId);
        }
      }
      // console.log("Audio output changed successfully");
    } catch (error) {
      console.error("Error changing audio output:", error);
    }
  };
  useEffect(() => {
    if (selectedAudioOutput) {
      filteredRemoteUsers.forEach(async (user) => {
        if (user.audioTrack) {
          try {
            await user.audioTrack.setPlaybackDevice(selectedAudioOutput);
          } catch (error) {
            console.error(
              "Error updating audio output for user:",
              user.uid,
              error
            );
          }
        }
      });
    }
  }, [selectedAudioOutput, filteredRemoteUsers]);

//   useEffect(() => {
//   if (currentAudioTrack) {
//     const audioTrackLabel = currentAudioTrack.getTrackLabel();
//     console.log('🎤 Currently ACTIVE Audio Input Track:', {
//       trackLabel: audioTrackLabel,
//       trackId: currentAudioTrack.getTrackId(),
//       enabled: currentAudioTrack.enabled,
//       muted: currentAudioTrack.muted,
//       timestamp: new Date().toISOString()
//     });
//   }
// }, [currentAudioTrack]);

// useEffect(() => {
//   if (currentVideoTrack) {
//     const videoTrackLabel = currentVideoTrack.getTrackLabel();
//     console.log('📹 Currently ACTIVE Camera Track:', {
//       trackLabel: videoTrackLabel,
//       trackId: currentVideoTrack.getTrackId(),
//       enabled: currentVideoTrack.enabled,
//       muted: currentVideoTrack.muted,
//       timestamp: new Date().toISOString()
//     });
//   }
// }, [currentVideoTrack]);

  useEffect(() => {
  const fetchDevices = async () => {
    try {
      // Fetch audio input devices
      const audioDevices = await AgoraRTC.getMicrophones();

      // Find default devices
      const defaultAudioInput = audioDevices.find(
        (d) => d.deviceId === "default"
      );

      // 音频输入设备 - 添加去重逻辑
      const filteredAudioInputDevices = audioDevices
        .filter((d) => d.deviceId !== "default")
        .filter(
          (device, index, self) =>
            index === self.findIndex((d) => d.groupId === device.groupId)
        )
        .map((device) => ({
          deviceId: device.deviceId,
          ...device,
          label: device.label
            .replace(/default|communications/gi, "")
            .replace(/-/g, "")
            .trim(),
        }));
      setAudioInputDevices(filteredAudioInputDevices);

      // Fetch audio output devices
      const audioOutput = await AgoraRTC.getPlaybackDevices();

      const defaultAudioOutput = audioOutput.find(
        (d) => d.deviceId === "default"
      );

      // 音频输出设备 - 保持去重
      const filteredAudioOutputDevices = audioOutput
        .filter((d) => d.deviceId !== "default")
        .filter(
          (device, index, self) =>
            index === self.findIndex((d) => d.groupId === device.groupId)
        )
        .map((device) => ({
          deviceId: device.deviceId,
          ...device,
          label: device.label
            .replace(/default|communications/gi, "")
            .replace(/-/g, "")
            .trim(),
        }));
      setAudioOutputDevices(filteredAudioOutputDevices);

      // Fetch camera devices
      const cameraDevices = await AgoraRTC.getCameras();

      const defaultCamera = cameraDevices.find(
        (d) => d.deviceId === "default"
      );

      // 摄像头设备 - 保持去重
      const filteredCameraDevices = cameraDevices
        .filter((d) => d.deviceId !== "default")
        .filter(
          (device, index, self) =>
            index === self.findIndex((d) => d.groupId === device.groupId)
        )
        .map((device) => ({
          deviceId: device.deviceId,
          ...device,
          label: device.label
            .replace(/default|communications/gi, "")
            .replace(/-/g, "")
            .trim(),
        }));
      setVideoDevices(filteredCameraDevices);

      // Store default device info for display
      const defaultDevicesInfo = {
        audioInput: defaultAudioInput,
        audioOutput: defaultAudioOutput,
        camera:
          defaultCamera ||
          (cameraDevices.length > 0 ? cameraDevices[0] : null),
      };

      setDefaultDevices(defaultDevicesInfo);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  fetchDevices();

  const handleDeviceChange = async () => {
    await fetchDevices();
  };

  navigator.mediaDevices.ondevicechange = handleDeviceChange;

  return () => {
    navigator.mediaDevices.ondevicechange = null;
  };
}, []);

  const handleClickOutside = (event) => {
    if (
      optionsDialogRef.current &&
      !optionsDialogRef.current.contains(event.target)
    ) {
      setShowOptionsDialog(false);
    } else if (
      translationDialogRef.current &&
      !translationDialogRef.current.contains(event.target)
    ) {
      setShowTranslation(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePreviewCancel = () => setShowOptionsDialog(false);

  useEffect(() => {
    startVideoCall();

    return () => {
      disconnectSignalR();
      cleanupResources();
    };
  }, []);

  const handleDragStart = (e) => {
    // Prevent default dragging behavior
    e.preventDefault();

    // Get mouse position
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (!clientX || !clientY) return;

    isDraggingRef.current = true;
    dragStartPos.current = { x: clientX, y: clientY };

    // Store initial position for calculation
    initialPos.current = { ...localVideoPosition };

    // Add event listeners for drag and end
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove, { passive: false });
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;

    e.preventDefault();

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (!clientX || !clientY) return;

    const deltaX = clientX - dragStartPos.current.x;
    const deltaY = clientY - dragStartPos.current.y;

    const containerRect = document
      .querySelector('[style*="position: relative"]')
      .getBoundingClientRect();

    let initRight = initialPos.current.right;
    let initTop = initialPos.current.top;

    if (typeof initRight === "string") {
      if (initRight.includes("px")) {
        initRight = parseInt(initRight);
      } else if (initRight.includes("%")) {
        initRight = (parseInt(initRight) / 100) * containerRect.width;
      }
    }

    if (typeof initTop === "string") {
      if (initTop.includes("px")) {
        initTop = parseInt(initTop);
      } else if (initTop.includes("%")) {
        initTop = (parseInt(initTop) / 100) * containerRect.height;
      }
    }

    const newRight = Math.max(
      0,
      Math.min(containerRect.width - 100, initRight - deltaX)
    );
    const newTop = Math.max(
      0,
      Math.min(containerRect.height - 100, initTop + deltaY)
    );

    setLocalVideoPosition({
      top: `${newTop}px`,
      right: `${newRight}px`,
      left: "auto",
      bottom: "auto",
    });
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;

    // Remove event listeners
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);
  };

  const handleUpdateActiveUsers = async (callerId) => {
    if (!callerId) {
      console.error("CallerId is required for updating active users");
      return;
    }

    try {
      const result = await fetchActiveUsers(callerId, effectiveUserId);

      if (result.success && result.data) {
        const { active_users } = result.data;

        // console.log("Fetched active users:", active_users);

        // 创建用户名映射
        const userNameMap = {};

        // 处理 active_users 数据
        active_users.forEach((user) => {
          if (user.extUserAgoraUid) {
            const uidKey = Number(user.extUserAgoraUid);
            userNameMap[uidKey] = {
              displayName: user.extUserName || "Unknown User",
              deviceId: user.deviceId,
              isInitiator: user.isInitiator,
              connectionId: user.connectionId,
            };
          }
        });

        // console.log("User name map:", userNameMap);
        // console.log("Remote users:", remoteUsers.map(u => u.uid));

        // 更新状态
        setActiveUserNameMap(userNameMap);
      } else {
        console.error("Failed to fetch active users:", result.error);
      }
    } catch (error) {
      console.error("Error updating active users:", error);
    }
  };

  const handleUserJoined = (user) => {
    if (isScreenShareUser(user.uid)) {
      setIsRemoteScreenSharing(true);
      setTimeout(() => {
        if (isRemoteScreenSharing && !screenShareUserName) {
          setScreenShareUserName("Someone");
        }
      }, 3000);
      return;
    }

    setRemoteUsers((prevUsers) => {
      if (!prevUsers.some((u) => u.uid === user.uid)) {
        return [...prevUsers, user];
      }
      return prevUsers;
    });

    if (isScreenSharingRef.current === true && clientRef.current) {
      setTimeout(() => {
        // Small delay to ensure the new user is fully connected
        try {
          const message = JSON.stringify({
            type: "SCREEN_SHARE_STATUS",
            userName: userInfo.name,
            uid: localAgoraUid,
            isSharing: true,
            timestamp: Date.now(),
          });

          clientRef.current.sendStreamMessage(message);
          console.log("Screen share status sent to new joiner:", message);
        } catch (error) {
          console.error(
            "Failed to send screen share status to new joiner:",
            error
          );
        }
      }, 1000);
    }

    // Log which device joined
    const deviceInfo = agoraUidToDeviceInfoMap[user.uid];
    if (deviceInfo) {
      // console.log(`Device joined: ${deviceInfo.name} (${deviceInfo.id})`);
    } else {
      // console.log(`Unknown user joined with UID: ${user.uid}`);
    }
    if (user?.uid === 1234 || user?.uid === 2000 || user?.uid === 3000) {
    } else {
      setCallState("connected");
        handleUpdateActiveUsers(callerIdRef.current);

        if (isVideoRecordingRef.current === true) {
            updateVideoRecordingState(callerIdRef.current)
        }
      // If the user is joining the call, show the subtitle and enable live translation automatically
      //  Show when the user joins behavior is set to "alwaysTranslate" or transcription is enabled and there are filtered remote users more than 0
      if (
        userProfile?.translateBehaviour === "alwaysTranslate" ||
        isTranscriptionEnabled
      ) {
        setTimeout(() => {
          setLiveTranslation(true);
        }, 5000); // 5000 ms = 5 seconds
      }
    }
  };

  const handleUserLeft = (user) => {
    if (isScreenShareUser(user.uid)) {
      setIsRemoteScreenSharing(false);
      setScreenShareUserName("");
      setRemoteScreenShareUser(null);
      return;
    }

      if (isVideoRecordingRef.current === true) {
          updateVideoRecordingState(callerIdRef.current)
      }

    setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));

    // Clean up active speaker state and timeouts
    setActiveSpeakers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(user.uid);
      return newSet;
    });

    if (speakerTimeoutsRef.current[user.uid]) {
      clearTimeout(speakerTimeoutsRef.current[user.uid]);
      delete speakerTimeoutsRef.current[user.uid];
    }

    const deviceInfo = agoraUidToDeviceInfoMapRef.current[user.uid];

    if (deviceInfo) {
      setInvitedParticipants((prev) => {
        if (prev.some((p) => p.id === deviceInfo.id)) return prev;
        return [
          ...prev,
          {
            id: deviceInfo.id,
            name: deviceInfo.name,
            profilePicture: deviceInfo.profilePicture || null,
            agoraUid: user.uid,
            status: "invited",
          },
        ];
      });
    }
  };

  useEffect(() => {
    if (localVideoRef.current && currentVideoTrack) {
      if (isCameraEnabled) {
        try {
          currentVideoTrack.play(localVideoRef.current);
          setLocalVideoEnabled(true);
        } catch (error) {
          console.error("Failed to play local video track:", error);
          setLocalVideoEnabled(false);
        }
      } else {
        currentVideoTrack.stop();
        setLocalVideoEnabled(false);
      }
    }
  }, [currentVideoTrack, isCameraEnabled]);

  useEffect(() => {
    if (currentVideoTrack && clientRef.current && joined) {
      handleTrackPublishing(currentVideoTrack, isCameraEnabled);
    }
  }, [isCameraEnabled, currentVideoTrack, joined]);

  useEffect(() => {
    if (currentAudioTrack && clientRef.current && joined) {
      handleTrackPublishing(currentAudioTrack, isMicrophoneEnabled);
    }
  }, [isMicrophoneEnabled, currentAudioTrack, joined]);

  useEffect(() => {
    if (
      remoteScreenShareUser &&
      remoteScreenShareUser.videoTrack &&
      screenVideoRef.current
    ) {
      // console.log("Playing remote screen share track in useEffect");
      try {
        remoteScreenShareUser.videoTrack.play(screenVideoRef.current);
      } catch (error) {
        console.error("Failed to play remote screen share:", error);
      }
    }
  }, [remoteScreenShareUser]);

  const handleTrackPublishing = async (track, isEnabled) => {
    if (!clientRef.current || !joined) return;

    try {
      if (isEnabled) {
        await track.setEnabled(true);
        await clientRef.current.publish(track);
      } else {
        await clientRef.current.unpublish(track);
        await track.setEnabled(false);
      }
    } catch (error) {
      console.error(
        `Failed to ${isEnabled ? "publish" : "unpublish"} ${
          track.trackMediaType
        } track:`,
        error
      );
    }
  };

  const cleanupResources = async () => {
    // console.log("Cleaning up resources...");

    stopCallingTone();
    Object.values(speakerTimeoutsRef.current).forEach((timeout) => {
      clearTimeout(timeout);
    });
    speakerTimeoutsRef.current = {};
    setActiveSpeakers(new Set());

    if (aiJoinedRef.current && transcriptionActiveRef.current) {
      try {
        console.log("Stopping transcription during cleanup...");
        await stopTranscription(callingDeviceId, userInfo.userId);
        console.log("Transcription stopped successfully");
      } catch (error) {
        console.error("Error stopping transcription during cleanup:", error);
      }
    }

    if (screenShareClientRef.current) {
      try {
        await screenShareClientRef.current.leave();
      } catch {}
      screenShareClientRef.current = null;
    }

    if (screenVideoTrackRef.current) {
      try {
        screenVideoTrackRef.current.stop();
        screenVideoTrackRef.current.close &&
          screenVideoTrackRef.current.close();
      } catch {}
      screenVideoTrackRef.current = null;
    }

    if (screenAudioTrackRef.current) {
      try {
        screenAudioTrackRef.current.stop();
        screenAudioTrackRef.current.close &&
          screenAudioTrackRef.current.close();
      } catch {}
      screenAudioTrackRef.current = null;
    }
    if (clientRef.current) {
      await clientRef.current.leave();
      clientRef.current.removeAllListeners();
      clientRef.current = null;
    }
    setRemoteUsers([]);
    setIsScreenSharing(false);
    isScreenSharingRef.current = false;
    setIsRemoteScreenSharing(false);
    setRemoteScreenShareUser(null);
    setScreenShareUserName("");
    setScreenShareClient(null);
    setScreenShareTrack(null);
    localUserIdRef.current = null;
    setJoined(false);
    setLiveTranslation(false);
    // console.log("Resources cleaned up");
  };

  async function startVideoCall() {
    if (isJoiningRef.current || localUserIdRef.current) {
      return;
    }

    isJoiningRef.current = true;

    try {
      if (isGroupCall) {
        const result = await initiateGroupCall(policyId, userInfo.name);

        if (!result.success) {
          throw new Error(result.error);
        }
        const obtainedCallerId = result.callerId;
        setCallerId(obtainedCallerId);
        callerIdRef.current = obtainedCallerId;
        await connectSignalR(
          obtainedCallerId,
          effectiveUserId,
          !isGuest,
          userInfo.name,
          localAgoraUid
        );

        startCallStatusCheck(
          obtainedCallerId,
          effectiveUserId,
          null,
          (isActive) => {
            // console.log("Call active status update:", isActive);
            // setCallActive(isActive);
          }
        );
      } else if (isEventCall) {
        const result = await initiateEventCall(eventId, callerName);

        if (!result.success) {
          throw new Error(result.error);
        }

        const obtainedCallerId = result.callerId;
        setCallerId(obtainedCallerId);
        callerIdRef.current = obtainedCallerId;

        await connectSignalR(
          obtainedCallerId,
          effectiveUserId,
          !isGuest,
          userInfo.name,
          localAgoraUid
        );

        signalRService.registerOnMuteMe(async (mute) => {
          if (currentAudioTrack) {
            await currentAudioTrack.setEnabled(!mute);
            setIsMicrophoneEnabled(!mute);
          }
        });

        // startCallStatusCheck(obtainedCallerId, effectiveUserId, null, (isActive) => {
        //   console.log("Call active status update:", isActive);
        //   // setCallActive(isActive);
        // });
      } else if (!isEventCall && !isGroupCall) {
        // Regular device call
        const result = await callDevice(
          callingDeviceId,
          userInfo.name,
          effectiveUserId,
          call_id
        );

        if (!result.success) {
          throw new Error(result.error);
        }
        const obtainedCallerId = result.callerId;

        setCallerId(obtainedCallerId);
        callerIdRef.current = obtainedCallerId;
        await connectSignalR(
          obtainedCallerId,
          effectiveUserId,
          !isGuest,
          userInfo.name, // ext_user_name
          localAgoraUid // ext_user_agora_uid
        );

        signalRService.registerOnMuteMe(async (mute) => {
          if (currentAudioTrack) {
            await currentAudioTrack.setEnabled(!mute);
            setIsMicrophoneEnabled(!mute);
          }
        });

        startCallStatusCheck(
          obtainedCallerId,
          effectiveUserId,
          callingDeviceId,
          (isActive) => {
            // console.log("Call active status update:", isActive);
            setCallActive(isActive);
          }
        );
      }

      let callType, id;

      if (isEventCall) {
        callType = "event";
        id = eventId;
      } else if (isGroupCall) {
        callType = "group";
        id = policyId;
      } else {
        callType = "device";
        id = callingDeviceId;
      }

      const tokenResult = await getCallToken(callType, id);

      if (!tokenResult.success) {
        throw new Error(tokenResult.error);
      }

      const token = tokenResult.token;
      if (!token) {
        throw new Error("No token received from server");
      }

      const client = AgoraRTC.createClient({ codec: "vp8", mode: "rtc" });
      clientRef.current = client;

      client.on("user-published", handleUserPublished);
      client.on("user-unpublished", handleUserUnpublished);
      client.on("user-joined", handleUserJoined);
      client.on("user-left", handleUserLeft);
      client.on("volume-indicator", handleVolumeIndicator);

      client.on("stream-message", (uid, data) => {
        try {
          // Handle different data types
          let messageText;
          if (typeof data === "string") {
            messageText = data;
          } else if (data instanceof Uint8Array) {
            messageText = new TextDecoder().decode(data);
          } else {
            messageText = data.toString();
          }

          const message = JSON.parse(messageText);
          console.log(
            "Received stream message from UID:",
            uid,
            "Message:",
            message
          );

          // Don't process our own messages
          if (message.uid === localAgoraUid) {
            return;
          }

          switch (message.type) {
            case "SCREEN_SHARE_START":
              console.log(`${message.userName} started screen sharing`);
              setScreenShareUserName(message.userName);
              setIsRemoteScreenSharing(true);
              break;

            case "SCREEN_SHARE_STOP":
              console.log(`${message.userName} stopped screen sharing`);
              setScreenShareUserName("");
              setIsRemoteScreenSharing(false);
              setRemoteScreenShareUser(null);
              break;

            case "SCREEN_SHARE_STATUS":
              // Handle status messages for new joiners
              if (message.isSharing) {
                console.log(
                  `${message.userName} is currently screen sharing (status update)`
                );
                setScreenShareUserName(message.userName);
                setIsRemoteScreenSharing(true);
              } else {
                console.log(
                  `${message.userName} is not screen sharing (status update)`
                );
                setScreenShareUserName("");
                setIsRemoteScreenSharing(false);
                setRemoteScreenShareUser(null);
              }
              break;

            default:
              console.log("Unknown stream message type:", message.type);
          }
        } catch (error) {
          console.error(
            "Failed to parse stream message:",
            error,
            "Raw data:",
            data
          );
        }
      });

      let channelId;
      if (isEventCall) {
        channelId = eventId;
      } else if (isGroupCall) {
        channelId = policyId;
      } else {
        channelId = callingAccountId;
      }

      await client.join(
        process.env.REACT_APP_AGORA_APP_ID,
        channelId,
        token,
        localAgoraUid
      );

      AgoraRTC.setParameter("AUDIO_VOLUME_INDICATION_INTERVAL", 500);
      await client.enableAudioVolumeIndicator();

      await client.enableDualStream({
        lowStreamConfig: {
          width: 160,
          height: 120,
          framerate: 15,
          bitrate: 100,
        },
      });

      localUserIdRef.current = localAgoraUid;
      setJoined(true);

      if (isCameraEnabled && currentVideoTrack) {
        await handleTrackPublishing(currentVideoTrack, true);
      }
      if (isMicrophoneEnabled && currentAudioTrack) {
        await handleTrackPublishing(currentAudioTrack, true);
      }
    } catch (error) {
      console.error("Failed to join channel", error);
    }

    isJoiningRef.current = false;
  }

  const handleUserPublished = async (user, mediaType) => {
    await clientRef.current.subscribe(user, mediaType);
    if (mediaType === "video") {
      if (isScreenShareUser(user.uid)) {
        setRemoteScreenShareUser(user);
        const playScreenShare = () => {
          if (screenVideoRef.current && user.videoTrack) {
            console.log("Playing remote screen share track");
            user.videoTrack.play(screenVideoRef.current);
          } else {
            console.log("Screen video ref not ready, retrying...");

            setTimeout(playScreenShare, 100);
          }
        };

        setTimeout(playScreenShare, 50);
        return;
      }
      setRemoteUsers((prevUsers) => {
        if (!prevUsers.some((u) => u.uid === user.uid)) {
          return [...prevUsers, user];
        }
        return prevUsers;
      });
      setRemoteCameraStates((prev) => ({ ...prev, [user.uid]: true }));
      if (remoteVideoRefs.current[user.uid]) {
        user.videoTrack.play(remoteVideoRefs.current[user.uid]);
      }
    }
    if (mediaType === "audio") {
      setRemoteUsers((prevUsers) => {
        if (!prevUsers.some((u) => u.uid === user.uid)) {
          return [...prevUsers, user];
        }
        return prevUsers;
      });
      setRemoteMicrophoneStates((prev) => ({ ...prev, [user.uid]: true }));
      try {
        if (selectedAudioOutput) {
          await user.audioTrack.setPlaybackDevice(selectedAudioOutput);
          // console.log("Playback device set successfully:", selectedAudioOutput);
        }
        user.audioTrack.play();
      } catch (error) {
        console.error("Error setting playback device:", error);
        user.audioTrack.play();
      }
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    if (mediaType === "video") {
      if (isScreenShareUser(user.uid)) {
        console.log("Screen share user unpublished video track:", user.uid);
        setRemoteScreenShareUser(null);
        return;
      }

      setRemoteCameraStates((prev) => ({ ...prev, [user.uid]: false }));
      if (user.videoTrack) {
        user.videoTrack.stop();
      }
    }
    if (mediaType === "audio") {
      setRemoteMicrophoneStates((prev) => ({ ...prev, [user.uid]: false }));
      if (user.audioTrack) {
        user.audioTrack.stop();
      }
    }
  };

  const handleToggleMute = async (participant) => {
    const willMute = participant.isMicOn;

    if (participant.connectionId) {
      try {
        await signalRService.toggleMute(participant.connectionId, willMute);

        setOnCallParticipants((prev) =>
          prev.map((p) =>
            p.connectionId === participant.connectionId
              ? { ...p, isMicOn: !willMute }
              : p
          )
        );
      } catch (err) {
        console.error("SignalR mute error", err);
      }
      return;
    }

    try {
      const response = await muteParticipant(participant.deviceId, willMute);

      if (response.success) {
        // console.log(
        //   `Successfully ${willMute ? "muted" : "unmuted"} device: ${participant.deviceId}`
        // );

        const agoraUid = deviceAgoraUidMap[participant.deviceId];
        if (agoraUid) {
          setRemoteMicrophoneStates((prev) => ({
            ...prev,
            [agoraUid]: !willMute,
          }));

          setOnCallParticipants((prev) => {
            return prev.map((p) =>
              p.deviceId === participant.deviceId
                ? { ...p, isMicOn: !willMute }
                : p
            );
          });
        }
      } else {
      }
    } catch (error) {
      console.error(`Error in handleToggleMute:`, error);
    }
  };

  const handleLeaveChannel = async () => {
    stopCallingTone();
    // console.log("Leaving channel...");
    try {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }

      await disconnectSignalR();

      await cleanupResources();
      if (isMobile) {
        window.history.back();
      } else {
        setLiveTranslation(false); // Off the transcription when leaving the call
        onClose();
      }
    } catch (error) {
      console.error("Failed to leave channel", error);
    }
  };

  const toggleCamera = async () => {
    if (currentVideoTrack) {
      const newState = !isCameraEnabled;
      setIsCameraEnabled(newState);

      try {
        await handleTrackPublishing(currentVideoTrack, newState);

        if (newState) {
          if (localVideoRef.current) {
            currentVideoTrack.play(localVideoRef.current);
            setLocalVideoEnabled(true);
          } else {
            console.warn("Local video container is not ready");
            setLocalVideoEnabled(false);
          }
        } else {
          currentVideoTrack.stop();
          setLocalVideoEnabled(false);
        }
      } catch (error) {
        console.error("Failed to toggle camera:", error);
        setIsCameraEnabled(!newState); // Revert the state if there's an error
        setLocalVideoEnabled(!newState);
      }
    }
  };

    const toggleVideoRecording = async () => {
        if (isVideoRecording === true) {
            await stopVideoRecording(callerId)
        }
        else {
            await startVideoRecording(callerId)
        }

        const newState = !(isVideoRecording);
        setisVideoRecording(newState)
        isVideoRecordingRef.current = newState;
    };

  const toggleMicrophone = async () => {
    if (currentAudioTrack) {
      const newState = !isMicrophoneEnabled;
      setIsMicrophoneEnabled(newState);
      await handleTrackPublishing(currentAudioTrack, newState);
    }
  };

  const toggleParticipantsPanel = () => {
    setShowParticipants(!showParticipants);
  };

  // Handle tab change in participants panel
  const handleParticipantTabChange = (event, newValue) => {
    setParticipantTabValue(newValue);
  };

  const renderVideoContainer = (
    videoTrack,
    label,
    isLocal = false,
    uid = null
  ) => {
    const isSpeaking = !isLocal && activeSpeakers.has(uid);

    return (
      <div
        style={{
          width: isLocal ? (isMobile ? "30%" : "25%") : "100%",
          height: isLocal ? (isMobile ? "30%" : "25%") : "100%",
          position: isLocal ? "absolute" : "relative",
          top: isLocal ? localVideoPosition.top : "auto",
          right: isLocal ? localVideoPosition.right : "auto",
          left: isLocal ? localVideoPosition.left : "auto",
          bottom: isLocal ? localVideoPosition.bottom : "auto",
          zIndex: isLocal ? 2 : 1,
          borderRadius: isMobile ? "8px" : "12px",
          overflow: "hidden",
          boxShadow: isLocal ? "0 4px 12px rgba(0,0,0,0.2)" : "none",
          display: isLocal && !showLocalVideo ? "none" : "block",
          cursor: isLocal ? "move" : "default",
          border: isSpeaking ? "3px solid #4286F4" : "3px solid transparent",
          animation: isSpeaking
            ? "speakerPulse 1.5s ease-in-out infinite"
            : "none",
        }}
        onMouseDown={isLocal ? handleDragStart : undefined}
        onTouchStart={isLocal ? handleDragStart : undefined}
      >
        {isSpeaking && (
          <style>
            {`
                        @keyframes speakerPulse {
                            0% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0.7); }
                            50% { border-color: #4286F4; box-shadow: 0 0 0 10px rgba(66, 134, 244, 0); }
                            100% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0); }
                        }
                    `}
          </style>
        )}
        <div
          style={{
            backgroundColor: "#1a1a1a",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            borderRadius: "12px",
          }}
        >
          <p
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              color: "white",
              fontSize: isLocal ? "12px" : "16px",
              margin: 0,
              zIndex: 3,
              backgroundColor: "rgba(0,0,0,0.5)",
              padding: "5px 10px",
              borderRadius: "20px",
            }}
          >
            {label}
          </p>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {isLocal ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: localVideoEnabled ? "block" : "none",
                }}
                ref={setLocalVideoContainer}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                ref={(ref) => {
                  if (ref && videoTrack) {
                    remoteVideoRefs.current[videoTrack.getUserId()] = ref;
                    videoTrack.play(ref);
                  }
                }}
              />
            )}
            {isLocal && !localVideoEnabled && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#2c3e50",
                  color: "white",
                  fontSize: isLocal ? "14px" : "24px",
                  fontWeight: "bold",
                }}
              >
                {userInfo.profilePicture ? (
                  <img
                    src={`data:image/png;base64, ${userInfo.profilePicture}`}
                    alt={userInfo.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "60%",
                      height: "60%",
                      maxWidth: "80px",
                      maxHeight: "80px",
                      borderRadius: "50%",
                      backgroundColor: getRandomColor(userInfo.name),
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "20px",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {userInfo.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRemoteVideos = (remoteUsers, hasScreenShare = false) => {
    const forceVerticalLayout = isMobile && remoteUsers.length === 2;

    const { cols, rows } = forceVerticalLayout
      ? { cols: 1, rows: 2 }
      : getGridLayout(remoteUsers.length, hasScreenShare);

    const gapSize = hasScreenShare
      ? 2
      : isMobile
      ? 4
      : Math.max(4, Math.min(10, 20 - Math.max(cols, rows)));
    const containerPadding = hasScreenShare ? 4 : isMobile ? 4 : gapSize;

    const getFontSize = (numUsers) => {
      if (hasScreenShare) {
        return isMobile ? 10 : 12;
      }

      if (isMobile) {
        if (numUsers <= 4) return 12;
        if (numUsers <= 9) return 10;
        return 8;
      } else {
        if (numUsers <= 4) return 16;
        if (numUsers <= 9) return 14;
        if (numUsers <= 16) return 12;
        return 10;
      }
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: `${gapSize}px`,
          width: "100%",
          height: "100%",
          padding: `${containerPadding}px`,
          overflow: "hidden",
          backgroundColor: hasScreenShare ? "black" : "black",
        }}
      >
        {remoteUsers.map((user) => {
          const isSpeaking = activeSpeakers.has(user.uid);
          return (
            <div
              key={user.uid}
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                backgroundColor: "#1a1a1a",
                borderRadius: hasScreenShare ? "6px" : "8px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: isSpeaking
                  ? "3px solid #4286F4"
                  : "3px solid transparent",
                animation: isSpeaking
                  ? "speakerPulse 1.5s ease-in-out infinite"
                  : "none",
                boxSizing: "border-box",
              }}
            >
              {isSpeaking && (
                <style>
                  {`
                        @keyframes speakerPulse {
                            0% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0.7); }
                            50% { border-color: #4286F4; box-shadow: 0 0 0 10px rgba(66, 134, 244, 0); }
                            100% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0); }
                        }
                    `}
                </style>
              )}
              <div
                style={{
                  position: "absolute",
                  top: "5px",
                  left: "5px",
                  zIndex: 3,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  padding: "4px 8px",
                  borderRadius: "8px",
                  maxWidth: "90%",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontSize: `${getFontSize(remoteUsers.length)}px`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {activeUserNameMap[user.uid]?.isInitiator
                    ? `${
                        activeUserNameMap[user.uid]?.displayName ||
                        agoraUidToDeviceInfoMap[user.uid]?.name ||
                        answerName ||
                        `Guest User`
                      } (Host)`
                    : activeUserNameMap[user.uid]?.displayName ||
                      agoraUidToDeviceInfoMap[user.uid]?.name ||
                      answerName ||
                      `Guest User`}
                </span>
              </div>

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
                ref={(ref) => {
                  if (ref && user.videoTrack && !user.videoTrack.isScreenTrack) {
                    remoteVideoRefs.current[user.uid] = ref;
                    user.videoTrack.play(ref);
                  }
                }}
              />

            {!remoteCameraStates[user.uid] && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#2c3e50",
                  color: "white",
                  fontSize: `${getFontSize(remoteUsers.length)}px`,
                  fontWeight: "bold",
                }}
              >
                {agoraUidToDeviceInfoMap[user.uid]?.profilePicture ? (
                  <img
                    src={`data:image/png;base64, ${
                      agoraUidToDeviceInfoMap[user.uid].profilePicture
                    }`}
                    alt={agoraUidToDeviceInfoMap[user.uid].name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: hasScreenShare ? "40%" : "60%",
                      height: hasScreenShare ? "40%" : "60%",
                      maxWidth: hasScreenShare ? "40px" : "120px",
                      maxHeight: hasScreenShare ? "40px" : "120px",
                      borderRadius: "50%",
                      backgroundColor: getRandomColor(
                        agoraUidToDeviceInfoMap[user.uid]?.name || "User"
                      ),
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: `${
                        getFontSize(remoteUsers.length) *
                        (hasScreenShare ? 1 : 2)
                      }px`,
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {(agoraUidToDeviceInfoMap[user.uid]?.name || "User")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </div>
         );
        })}
      </div>
    );
  };

  const buttonStyle = (enabled) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: isMobile ? "40px" : "50px",
    height: isMobile ? "40px" : "50px",
    margin: isMobile ? "0 5px" : "0 10px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: enabled ? "#1976D2" : "#1976D2",
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
    color: "white",
  });

  const liveTranslationStatusChange = (event) => {
    setLiveTranslation(event.target.checked);
    // show subtitle if live translation is enabled
    if (!event.target.checked) {
      setShowSubTitle(false);
    }
  };

  const showSubTitleChange = (event) => {
    setShowSubTitle(event.target.checked);
  };

  // Change your language from dropdown of transcription
  const changeYourLanguage = (event) => {
    setShowSetDefaultBtn(event.target.value !== yourPreferredLanguage); // Enable set default button if you change language your language
    setYourPreferredLanguage(event.target.value); // update your selected language

    // stop transcription if you change your language
    stopTranscription(callingDeviceId, userInfo.userId)
      .then(async () => {
        // Start transcription again with new language
        await startTranscription(
          callingDeviceId,
          userInfo.userId,
          selectedDevicePreferredLanguage,
          event.target.value
        );
      })
      .catch((error) => {
        console.error("Error stopping transcription:", error);
      });
  };

  // Change selected device language from dropdown of transcription
  const changeDeviceLanguage = (event) => {
    setShowSetDefaultBtn(
      event.target.value !== selectedDevicePreferredLanguage
    ); // Enable set default button if you change language device language
    setSelectedDevicePreferredLanguage(event.target.value); // update selected device language

    // stop transcription if you change your language
    stopTranscription(callingDeviceId, userInfo.userId)
      .then(() => {
        // Start transcription again with new language
        startTranscription(
          callingDeviceId,
          userInfo.userId,
          event.target.value,
          yourPreferredLanguage
        );
      })
      .catch((error) => {
        console.error("Error stopping transcription:", error);
      });
  };

  // Save as default settings of transcription
  const handleSaveAsDefaultSettings = async () => {
    // Update profile language API call and also update local storage
    await updateUserProfile(
      userInfo.userId,
      yourPreferredLanguage,
      userProfile.translateBehaviour
    )
      .then(async () => {
        userProfile.preferredLang = yourPreferredLanguage;
        localStorage.setItem("userInfo", JSON.stringify(userProfile));
      })
      .catch((error) => {
        console.error("Error updating user profile:", error);
      });

    // Update device language Api call
    await updateDeviceProfile(callingDeviceId, selectedDevicePreferredLanguage);

    setShowTranslation(false); // Close translation settings panel
  };

  const hasAnyScreenShare =
    isScreenSharing === true || isRemoteScreenSharing || remoteScreenShareUser;

  return (
    <div
  style={{
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "black",
    boxSizing: "border-box",
    display: "flex", 
    overflow: "hidden",
  }}
>
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      width: showParticipants && !isMobile 
        ? "calc(100% - 360px)"
        : "100%", 
      height: "100%",
      transition: "width 0.3s ease",
    }}
  >
    {/* Video container*/}
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "calc(100% - 80px)", 
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: "black",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          maxWidth: hasAnyScreenShare ? "none" : "calc(16/9 * 100vh - 80px)",
          maxHeight: "100%",
          position: "relative",
        }}
      >
        {hasAnyScreenShare ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "row",
            }}
          >

            <div
              style={{

                width: isMobile ? "100%" : "75%",
                height: "100%",
                backgroundColor: "black",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  zIndex: 3,
                  backgroundColor: "#131314",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                }}
              >
                {isScreenSharing
                  ? `${userInfo.name} (You) - Screen Share`
                  : `${screenShareUserName || "Someone"} - Screen Share`}
              </div>

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  backgroundColor:"black"
                }}
                ref={(ref) => {
                  if (ref) {
                    screenVideoRef.current = ref;

                    if (
                      !isScreenSharing &&
                      remoteScreenShareUser?.videoTrack
                    ) {
                      try {
                        remoteScreenShareUser.videoTrack.play(ref);
                      } catch (error) {
                        console.error(
                          "Failed to play remote screen share immediately:",
                          error
                        );
                      }
                    }
                  }
                }}
              />
            </div>

        
            {!isMobile && (
              <div
                style={{
                  width: "25%", 
                  height: "100%",
                  backgroundColor: "black",
                  borderLeft: "0px solid #131314",
                  display: "flex",
                  flexDirection: "column",
                  padding: "8px",
                  gap: "8px",
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#555 #2a2a2a",
                  marginRight: "2%",
                }}
              >
                
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "25vh",
                    backgroundColor: "black",
                    borderRadius: "8px",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    boxSizing: "border-box",
                    margin: "0 auto", 

                  }}
                >

                  
                  <div
                    style={{
                      position: "absolute",
                      top: "5px",
                      left: "5px",
                      zIndex: 3,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      maxWidth: "90%",
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                    >
                      {`${userInfo.name} (You)`}
                    </span>
                  </div>

                  <div
                    style={{
                      width: "100%", 
                      height: "100%",
                    
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      overflow: "hidden",
                    }}
                  >
                    {localVideoEnabled ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        ref={setLocalVideoContainer}
                      />
                    ) : (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "#2c3e50",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {userInfo.profilePicture ? (
                          <img
                            src={`data:image/png;base64, ${userInfo.profilePicture}`}
                            alt={userInfo.name}
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              backgroundColor: getRandomColor(userInfo.name),
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: "18px",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          >
                            {userInfo.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

              
                {filteredRemoteUsers.map((user, index) => (
                  <div
                    key={user.uid}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "25vh",
                      backgroundColor: "black",
                      borderRadius: "8px",
                      overflow: "hidden",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                      boxSizing: "border-box",
                      margin: "0 auto", 
                      border: activeSpeakers.has(user.uid) ? "3px solid #4286F4" : "3px solid transparent",
                      animation: activeSpeakers.has(user.uid) ? "speakerPulse 1.5s ease-in-out infinite" : "none",
                    }}
                  >

                    {activeSpeakers.has(user.uid) && (
                    <style>
                      {`
                        @keyframes speakerPulse {
                          0% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0.7); }
                          50% { border-color: #4286F4; box-shadow: 0 0 0 10px rgba(66, 134, 244, 0); }
                          100% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0); }
                        }
                      `}
                    </style>
                  )}
                    <div
                      style={{
                        position: "absolute",
                        top: "5px",
                        left: "5px",
                        zIndex: 3,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        maxWidth: "90%",
                      }}
                    >
                      <span
                        style={{
                          color: "white",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {activeUserNameMap[user.uid]?.isInitiator
                          ? `${
                              activeUserNameMap[user.uid]?.displayName ||
                              agoraUidToDeviceInfoMap[user.uid]?.name ||
                              answerName ||
                              `Guest User`
                            } (Host)`
                          : activeUserNameMap[user.uid]?.displayName ||
                            agoraUidToDeviceInfoMap[user.uid]?.name ||
                            answerName ||
                            `Guest User`}
                      </span>
                    </div>

                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                          ref={(ref) => {
                            if (
                              ref &&
                              user.videoTrack &&
                              !user.videoTrack.isScreenTrack
                            ) {
                              remoteVideoRefs.current[user.uid] = ref;
                              user.videoTrack.play(ref);
                            }
                          }}
                        />

                        {!remoteCameraStates[user.uid] && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "#2c3e50",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            {agoraUidToDeviceInfoMap[user.uid]
                              ?.profilePicture ? (
                              <img
                                src={`data:image/png;base64, ${
                                  agoraUidToDeviceInfoMap[user.uid]
                                    .profilePicture
                                }`}
                                alt={agoraUidToDeviceInfoMap[user.uid].name}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  borderRadius: "50%",
                                  backgroundColor: getRandomColor(
                                    agoraUidToDeviceInfoMap[user.uid]?.name ||
                                      "User"
                                  ),
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  fontSize: "18px",
                                  color: "white",
                                  fontWeight: "bold",
                                }}
                              >
                                {(
                                  agoraUidToDeviceInfoMap[user.uid]?.name ||
                                  "User"
                                )
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {isMobile && (
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      width: "120px",
                      maxHeight: "calc(100% - 20px)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      zIndex: 2,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "80px",
                        backgroundColor: "#1a1a1a",
                        borderRadius: "8px",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "2px",
                          left: "2px",
                          zIndex: 3,
                          backgroundColor: "rgba(0,0,0,0.7)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          maxWidth: "90%",
                        }}
                      >
                        <span
                          style={{
                            color: "white",
                            fontSize: "10px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                          }}
                        >
                          You
                        </span>
                      </div>

                      {localVideoEnabled ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          ref={setLocalVideoContainer}
                        />
                      ) : (
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: getRandomColor(userInfo.name),
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "14px",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {userInfo.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                {filteredRemoteUsers.slice(0, 4).map((user) => (
                  <div
                    key={user.uid}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "80px",
                      backgroundColor: "#1a1a1a",
                      borderRadius: "8px",
                      overflow: "hidden",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      border: activeSpeakers.has(user.uid) ? "2px solid #4286F4" : "2px solid transparent",
                      animation: activeSpeakers.has(user.uid) ? "speakerPulse 1.5s ease-in-out infinite" : "none",
                    }}
                  >

                    {activeSpeakers.has(user.uid) && (
                    <style>
                      {`
                        @keyframes speakerPulse {
                          0% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0.7); }
                          50% { border-color: #4286F4; box-shadow: 0 0 0 10px rgba(66, 134, 244, 0); }
                          100% { border-color: #4286F4; box-shadow: 0 0 0 0 rgba(66, 134, 244, 0); }
                        }
                      `}
                    </style>
                  )}
                    <div
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: "2px",
                        zIndex: 3,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        maxWidth: "90%",
                      }}
                    >
                      <span
                        style={{
                          color: "white",
                          fontSize: "10px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {activeUserNameMap[user.uid]?.displayName ||
                          agoraUidToDeviceInfoMap[user.uid]?.name ||
                          "Guest"}
                      </span>
                    </div>

                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                          ref={(ref) => {
                            if (
                              ref &&
                              user.videoTrack &&
                              !user.videoTrack.isScreenTrack
                            ) {
                              remoteVideoRefs.current[user.uid] = ref;
                              user.videoTrack.play(ref);
                            }
                          }}
                        />

                        {!remoteCameraStates[user.uid] && (
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              backgroundColor: getRandomColor(
                                agoraUidToDeviceInfoMap[user.uid]?.name ||
                                  "User"
                              ),
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: "14px",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          >
                            {(agoraUidToDeviceInfoMap[user.uid]?.name || "User")
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : filteredRemoteUsers.length > 0 ? (
              renderRemoteVideos(filteredRemoteUsers)
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  fontSize: isMobile ? "18px" : "24px",
                }}
              >
                {callState === "waiting"
                  ? "Waiting for devices to join..."
                  : "The other participant has left the call."}
              </div>
            )}

            {showSubTitle && remoteUsers.length === 2 && (
              <div
                style={{
                  position: "absolute",
                  inset: "20px auto auto 20px",
                  maxWidth: !isMobile ? "65%" : "60%",
                  width: "100%",
                  border: "1px solid #9F9F9F",
                  borderRadius: "10px",
                  background: "rgba(0, 0, 0, 0.5)",
                  zIndex: "2",
                  maxHeight: "25%",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                }}
              >
                <p
                  style={{
                    fontSize: "clamp(14px, 2.5vw + 1vh, 36px)",
                    lineHeight: "100%",
                    padding: "20px",
                    color: "#FFFFFF",
                  }}
                >
                  {messages}
                </p>
              </div>
            )}

            {!hasAnyScreenShare &&
              renderVideoContainer(
                currentVideoTrack,
                `${userInfo.name} (You)`,
                true
              )}

            {liveTranslation && remoteUsers.length >= 1 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  maxWidth: "160px",
                  alignItems: "center",
                  position: "absolute",
                  bottom: "14px",
                  left: "0",
                }}
              >
                <figure
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  <img
                    style={{
                      borderRadius: "50%",
                      width: "96px",
                      height: "96px",
                      objectFit: "cover",
                    }}
                    src={SideImage}
                    alt="Default profile"
                  />
                </figure>
                <div>
                  <h3
                    style={{
                      borderRadius: "8px",
                      background: "rgba(0, 0, 0, 0.5)",
                      padding: "11px",
                      width: "100%",
                      maxWidth: "126px",
                      fontSize: "12px",
                      color: "#FFFFFF",
                    }}
                  >
                    {remoteUsers.length === 2
                      ? "A.I. Translator"
                      : "Waiting for A.I. Translator"}
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar - fixed at bottom of left container */}
        <div
          style={{
            width: "100%",
            height: "80px",
            backgroundColor: "#131314",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: isMobile ? "12px" : "20px",
              padding: "0 16px",
            }}
          >
            {!isMobile && !isGuest && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={toggleParticipantsPanel}
                  style={{
                    ...buttonStyle(showParticipants),
                    margin: 0,
                    backgroundColor: showParticipants ? "#1976D2" : "#555",
                  }}
                  aria-label="Toggle Participants Panel"
                >
                  <PeopleRounded />
                </button>
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={toggleMicrophone}
                style={{
                  ...buttonStyle(isMicrophoneEnabled),
                  margin: 0,
                  backgroundColor: isMicrophoneEnabled ? "#1976D2" : "#555",
                }}
                aria-label="Toggle Microphone"
              >
                {isMicrophoneEnabled ? (
                  <KeyboardVoiceRounded />
                ) : (
                  <MicOffRounded />
                )}
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={toggleCamera}
                style={{
                  ...buttonStyle(isCameraEnabled),
                  margin: 0,
                  backgroundColor: isCameraEnabled ? "#1976D2" : "#555",
                }}
                aria-label="Toggle Camera"
              >
                {isCameraEnabled ? <VideocamRounded /> : <VideocamOffRounded />}
              </button>
            </div>

                      {isVideoRecordingAllowed &&
                          <div
                              style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                              }}
                          >
                              <button
                                  onClick={toggleVideoRecording}
                                  style={{
                                      ...buttonStyle(isVideoRecording),
                                      margin: 0,
                                      backgroundColor: isVideoRecording ? "#ff0000" : "#555",
                                  }}
                                  aria-label="Record Video"
                              >
                                  {isVideoRecording ? <RadioButtonChecked /> : <RadioButtonCheckedRounded />}
                              </button>
                          </div>
                      }

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => setShowLocalVideo(!showLocalVideo)}
                style={{
                  ...buttonStyle(showLocalVideo),
                  margin: 0,
                  backgroundColor: showLocalVideo ? "#1976D2" : "#555",
                }}
                aria-label="Toggle Local Video Preview"
              >
                <PictureInPictureRounded />
              </button>
            </div>

            <div
              style={{
                display: "none",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => setShowTranslation(!showTranslation)}
                style={{
                  ...buttonStyle(showTranslation),
                  margin: 0,
                }}
                aria-label="Toggle Translation"
              >
                <Translate />
              </button>
            </div>

           
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                  disabled={
                    (isRemoteScreenSharing && !isScreenSharing) ||
                    isScreenSharing === "loading"
                  }
                  style={{
                    ...buttonStyle(isScreenSharing === true),
                    margin: 0,
                    backgroundColor:
                      isScreenSharing === true
                        ? "#555"
                        : (isRemoteScreenSharing && !isScreenSharing) ||
                          isScreenSharing === "loading"
                        ? "#666"
                        : "#1976D2",
                    opacity:
                      (isRemoteScreenSharing && !isScreenSharing) ||
                      isScreenSharing === "loading"
                        ? 0.5
                        : 1,
                    cursor:
                      (isRemoteScreenSharing && !isScreenSharing) ||
                      isScreenSharing === "loading"
                        ? "not-allowed"
                        : "pointer",
                  }}
                  aria-label={
                    isScreenSharing === "loading"
                      ? "Starting screen share..."
                      : isScreenSharing === true
                      ? "You are presenting"
                      : isRemoteScreenSharing && !isScreenSharing
                      ? `${screenShareUserName} is presenting`
                      : "Start Screen Share"
                  }
                  title={
                    isScreenSharing === "loading"
                      ? "Starting screen share..."
                      : isScreenSharing === true
                      ? "You are presenting"
                      : isRemoteScreenSharing && !isScreenSharing
                      ? `${screenShareUserName} is presenting`
                      : "Start Screen Share"
                  }
                >
                  {isScreenSharing === true ? (
                    <StopScreenShareRounded />
                  ) : (
                    <ScreenShareRounded />
                  )}
                </button>
              </div>
            

            {!isMobile && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() => {
                    setShowOptionsDialog(true);
                  }}
                  style={{
                    ...buttonStyle(true),
                    margin: 0,
                    transform: "rotate(90deg)",
                  }}
                  aria-label="More Options"
                >
                  <MoreVert />
                </button>
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleLeaveChannel}
                style={{
                  ...buttonStyle(true),
                  margin: 0,
                  backgroundColor: "rgba(211, 47, 47, 0.8)",
                }}
                aria-label="End Call"
              >
                <CallEndRounded />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right container - participants panel */}
      {showParticipants && !isMobile && (
        <div
          style={{
            width: "360px",
            height: "100%",
            backgroundColor: "#fff",
            borderLeft: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div" style={{ color: "black" }}>
              Participants
            </Typography>
            <IconButton
              onClick={() => setShowParticipants(false)}
              size="small"
              aria-label="close participants panel"
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseRounded />
            </IconButton>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={participantTabValue}
              onChange={handleParticipantTabChange}
              aria-label="participants tabs"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  minWidth: "120px", // Make tabs wider
                },
              }}
            >
              <Tab label="On Call" />
              <Tab label="Invited" />
            </Tabs>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {participantTabValue === 0 && (
              <List>
                {onCallParticipants.map((participant) => (
                  <ListItem
                    key={participant.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      px: 2,
                    }}
                  >
                    {/* Avatar */}
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      {participant.profilePicture ? (
                        <Avatar
                          src={`data:image/png;base64, ${participant.profilePicture}`}
                          alt={participant.name}
                        />
                      ) : (
                        <Avatar
                          style={{
                            backgroundColor: getRandomColor(participant.name),
                          }}
                        >
                          {participant.name.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                    </ListItemAvatar>

                    {/* User Name - Fixed width for consistent alignment */}
                    <Box
                      sx={{
                        width: "100px", // Adjusted for narrower panel
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mr: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "medium",
                        }}
                      >
                        {participant.name}
                      </Typography>
                    </Box>

                    {/* Status Icons - Now they'll always appear at the same position */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {/* Mic status icon */}
                      <Box
                        sx={{
                          color: participant.isMicOn ? "#000000" : "#F44336",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                        }}
                      >
                        {participant.isMicOn ? (
                          <KeyboardVoiceRounded fontSize="small" />
                        ) : (
                          <MicOffRounded fontSize="small" />
                        )}
                      </Box>

                      {/* Camera status icon */}
                      <Box
                        sx={{
                          color: participant.isCameraOn ? "#000000" : "#F44336",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                          ml: 1,
                        }}
                      >
                        {participant.isCameraOn ? (
                          <VideocamRounded fontSize="small" />
                        ) : (
                          <VideocamOffRounded fontSize="small" />
                        )}
                      </Box>
                    </Box>

                    {/* Mute/Unmute button pushed to the right */}
                    {!participant.isLocal && !participant.isFamilyMember && (
                      <Box sx={{ ml: "auto" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleToggleMute(participant)}
                          sx={{
                            textTransform: "none",
                            borderColor: participant.isMicOn
                              ? "#666"
                              : "#1976D2",
                            color: participant.isMicOn ? "#666" : "#1976D2",
                            "&:hover": {
                              borderColor: participant.isMicOn
                                ? "#D32F2F"
                                : "#4CAF50",
                              backgroundColor: participant.isMicOn
                                ? "rgba(244, 67, 54, 0.04)"
                                : "rgba(76, 175, 80, 0.04)",
                            },
                            padding: "2px 8px", // Make button smaller for narrow panel
                            fontSize: "12px",
                          }}
                        >
                          {participant.isMicOn ? "Mute" : "Unmute"}
                        </Button>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            {participantTabValue === 1 && (
              <List>
                {invitedParticipants.map((participant) => (
                  <ListItem key={participant.id}>
                    <ListItemAvatar>
                      {participant.profilePicture ? (
                        <Avatar
                          src={`data:image/png;base64, ${participant.profilePicture}`}
                          alt={participant.name}
                        />
                      ) : (
                        <Avatar
                          style={{
                            backgroundColor: getRandomColor(participant.name),
                          }}
                        >
                          {participant.name.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={participant.name}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        noWrap: true,
                        style: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "80px", // Adjusted for narrower panel
                        },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CallRounded />}
                      onClick={() => handleCallAgain(participant.id)}
                      sx={{
                        textTransform: "none",
                        borderColor: "#1976D2",
                        color: "#1976D2",
                        "&:hover": {
                          borderColor: "#1565C0",
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                        padding: "3px 8px", // Smaller button
                        fontSize: "12px",
                      }}
                    >
                      Call
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </div>
      )}

      {/* Settings dialog */}
      {showOptionsDialog && (
        <div
          ref={optionsDialogRef}
          style={{
            position: "absolute",
            bottom: "90px", // Position above the toolbar
            left: showParticipants ? "44%" : "58%",
            transform: "translateX(-50%)",
            backgroundColor: "#f0f0f0",
            padding: isMobile ? "10px" : "15px",
            borderRadius: "8px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            zIndex: 10,
            maxWidth: isMobile ? "90%" : "300px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="audio-inputselect"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Audio Input
              </label>
              <select
                id="audio-input-select"
                value={selectedAudioInput}
                onChange={(e) => handleAudioInputChange(e)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              >
                <option value="">
                  System Default
                  {defaultDevices.audioInput &&
                    ` (${defaultDevices.audioInput.label})`}
                </option>
                {audioInputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || "Unnamed Audio Input"}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="audio-output-select"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Audio Output
              </label>
              <select
                id="audio-output-select"
                value={selectedAudioOutput}
                onChange={(e) =>
                  handleAudioOutputChange(e, setSelectedAudioOutput)
                }
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              >
                <option value="">
                  System Default
                  {defaultDevices.audioOutput &&
                    ` (${defaultDevices.audioOutput.label})`}
                </option>
                {audioOutputDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || "Unnamed Audio Output"}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ width: "100%", maxWidth: "350px" }}>
              <label
                htmlFor="camera-select"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Camera
              </label>
              <select
                id="camera-select"
                value={selectedVideo}
                onChange={(e) => handleCameraChange(e)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              >
                <option value="">
                  System Default
                  {defaultDevices.camera && ` (${defaultDevices.camera.label})`}
                </option>
                {videoDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || "Unnamed Camera"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "-10px", // Position the triangle at the bottom of the dialog box
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "10px solid #f0f0f0", // Triangle color matching the dialog background
            }}
          ></div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Button
              onClick={() => setShowOptionsDialog(false)}
              color="secondary"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Translation dialog */}
      {showTranslation && (
        <div
          ref={translationDialogRef}
          style={{
            position: "absolute",
            bottom: "90px", // Position above the toolbar
            left: showParticipants ? "40%" : "54%",
            transform: "translateX(-50%)",
            backgroundColor: "#f0f0f0",
            padding: isMobile ? "10px" : "15px",
            borderRadius: "8px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            zIndex: 10,
            maxWidth: isMobile ? "90%" : "300px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="live-translation"
                style={{
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Live Translation
              </label>
              <Switch
                id="live-translation"
                className="ml-lg-5"
                checked={liveTranslation}
                onClick={liveTranslationStatusChange}
              />
            </div>

            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="your-language"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Your Language
              </label>
              <select
                id="your-language"
                value={yourPreferredLanguage}
                onChange={(e) => changeYourLanguage(e)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
                disabled={!liveTranslation} // Disable if showSubTitle is false
              >
                {languages.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.key}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="device-language"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Device Language
              </label>
              <select
                id="device-language"
                value={selectedDevicePreferredLanguage}
                onChange={(e) => changeDeviceLanguage(e)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
                disabled={!liveTranslation} // Disable if showSubTitle is false
              >
                {languages.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.key}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{ width: "100%", maxWidth: "350px", marginBottom: "10px" }}
            >
              <label
                htmlFor="show-subtitles"
                style={{
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Show Subtitles
              </label>
              <Switch
                id="show-subtitles"
                className="ml-lg-5"
                checked={showSubTitle}
                onClick={showSubTitleChange}
                disabled={!liveTranslation}
              />
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "-10px", // Position the triangle at the bottom of the dialog box
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "10px solid #f0f0f0", // Triangle color matching the dialog background
            }}
          ></div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <button
              className="btn cur-p btn-primary"
              disabled={!showSetDefaultBtn}
              onClick={() => handleSaveAsDefaultSettings()}
            >
              Save as Default Settings
            </button>
            <Button onClick={() => setShowTranslation(false)} color="secondary">
              Close
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={showAlert}
        maxWidth={isMobile ? "xs" : "sm"}
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {callActive === false ? "Call Declined" : "Call Ending"}
        </DialogTitle>
        <DialogContent>
          <Typography id="alert-dialog-description">
            {callActive === false
              ? "The other participant has declined the call."
              : "The other participant has left the call."}
            The video call will close in {countdown}{" "}
            {countdown === 1 ? "second" : "seconds"}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLeaveChannel} color="primary" autoFocus>
            Close Now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 10000 }} // Ensure high z-index
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AgoraVideoCall;
